class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  /**
   * Animates free-shipping bar (--fsp-progress) and remaining amount when drawer opens or markup updates.
   *
   * Strategy: the bar's width is computed entirely from `--fsp-progress` (0..100) on the FSP root,
   * set by Liquid on every server render. The bar therefore renders correctly with zero JS — JS only
   * exists to smoothly animate from the previous value to the new value across DOM replacements.
   *
   * Previous values persist on `this` (the cart-drawer custom element) because the FSP root gets
   * replaced on every cart update; storing on the root itself would lose the prev value each time.
   */
  refreshFreeShippingProgress() {
    const root = this.querySelector('#CartDrawer-FreeShipping');
    if (!root) return;

    if (this._nicheAmountRaf) {
      cancelAnimationFrame(this._nicheAmountRaf);
      this._nicheAmountRaf = null;
    }

    const reducedMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targetPct = Math.min(100, Math.max(0, Number.parseFloat(root.dataset.fillPct || '0')));

    const fromPct =
      typeof this._nicheLastFillPct === 'number' && !Number.isNaN(this._nicheLastFillPct)
        ? this._nicheLastFillPct
        : targetPct;

    if (reducedMotion || fromPct === targetPct) {
      root.style.setProperty('--fsp-progress', String(targetPct));
    } else {
      // Start from previous value (no transition), force reflow, then animate to target.
      const fill = root.querySelector('.cart-free-shipping-progress__fill');
      root.style.setProperty('--fsp-progress', String(fromPct));
      if (fill) {
        const prev = fill.style.transition;
        fill.style.transition = 'none';
        fill.getBoundingClientRect();
        fill.style.transition = prev;
      }
      requestAnimationFrame(() => {
        root.style.setProperty('--fsp-progress', String(targetPct));
      });
    }
    this._nicheLastFillPct = targetPct;

    if (root.dataset.unlocked === 'true') {
      this._nicheLastRemainingCents = 0;
      return;
    }

    const amountEl = root.querySelector('.cart-free-shipping-progress__amount');
    const target = Number.parseInt(root.dataset.remainingCents, 10);
    if (!amountEl || Number.isNaN(target)) return;

    const locale = root.dataset.locale || 'en';
    const rawSuffix = root.dataset.amountSuffix || ' Dhs';
    const suffix = rawSuffix.startsWith(' ') ? rawSuffix : ` ${rawSuffix}`;

    const formatLine = (cents) => {
      const v = cents / 100;
      let intlLocale = 'en-US';
      if (locale === 'ar') intlLocale = 'ar-MA';
      else if (locale === 'fr') intlLocale = 'fr-FR';
      const isWhole = Math.abs(v - Math.round(v)) < 0.005;
      const opts = isWhole
        ? { maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
      let n;
      try {
        n = new Intl.NumberFormat(intlLocale, opts).format(v);
      } catch {
        n = isWhole ? String(Math.round(v)) : v.toFixed(2);
      }
      return `${n}${suffix}`;
    };

    const from =
      typeof this._nicheLastRemainingCents === 'number' && !Number.isNaN(this._nicheLastRemainingCents)
        ? this._nicheLastRemainingCents
        : target;

    if (reducedMotion || from === target) {
      amountEl.textContent = formatLine(target);
      this._nicheLastRemainingCents = target;
      return;
    }

    const duration = 260;
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) * (1 - t);
      const val = Math.round(from + (target - from) * eased);
      amountEl.textContent = formatLine(val);
      if (t < 1) {
        this._nicheAmountRaf = requestAnimationFrame(tick);
      } else {
        amountEl.textContent = formatLine(target);
        this._nicheLastRemainingCents = target;
        this._nicheAmountRaf = null;
      }
    };

    this._nicheAmountRaf = requestAnimationFrame(tick);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.refreshFreeShippingProgress());
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    if (this._nicheAmountRaf) {
      cancelAnimationFrame(this._nicheAmountRaf);
      this._nicheAmountRaf = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /**
   * Quantity-change render: swap only the items list, footer totals, and free-shipping
   * bar instead of re-rendering the whole `.drawer__inner`. Keeps scroll position, the
   * cart-note draft, and avoids image/web-component churn (so the estimated total + retail
   * total update without a heavy reflow). Falls back to a full inner replace when the cart
   * empties, since the empty state lives outside these regions.
   */
  renderUpdatedSections(parsedState) {
    if (parsedState.item_count === 0 || !parsedState.sections['cart-drawer']) {
      super.renderUpdatedSections(parsedState);
      return;
    }

    const sourceDoc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
    this.applyDrawerPartialUpdate(sourceDoc);

    const bubbleTarget = document.getElementById('cart-icon-bubble');
    if (bubbleTarget && parsedState.sections['cart-icon-bubble']) {
      const bubbleInner = bubbleTarget.querySelector('.shopify-section') || bubbleTarget;
      bubbleInner.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble'], '.shopify-section');
    }
  }

  /**
   * Swaps the cart items, footer totals, and free-shipping bar from a parsed
   * cart-drawer section document. Uses innerHTML for the items container to
   * avoid tearing down and re-upgrading the cart-drawer-items custom element
   * (and all its children: quantity-input, cart-remove-button, etc.), which
   * was the main perf bottleneck. The footer and free-shipping bar use
   * replaceWith since they are plain divs with no custom-element overhead.
   */
  applyDrawerPartialUpdate(sourceDoc) {
    // 1. Items list – swap inner content only, preserving the custom element shell.
    const sourceItems = sourceDoc.querySelector('cart-drawer-items');
    if (sourceItems) {
      this.innerHTML = sourceItems.innerHTML;
    }

    // Helper for plain-div selectors below
    const getEl = (sel, doc) =>
      sel.startsWith('#') ? doc.getElementById(sel.slice(1)) : doc.querySelector(sel);

    // 2. Footer totals (estimated total, discounts, tax note)
    const footerTarget = getEl('.cart-drawer__footer', document);
    const footerSource = getEl('.cart-drawer__footer', sourceDoc);
    if (footerTarget && footerSource) {
      footerTarget.replaceWith(footerSource);
    }

    // 3. Free-shipping bar + trust props
    const fspTarget = getEl('#CartDrawer-FreeShipping', document);
    const fspSource = getEl('#CartDrawer-FreeShipping', sourceDoc);
    if (fspTarget && fspSource) {
      fspTarget.replaceWith(fspSource);
    } else if (fspTarget && !fspSource) {
      fspTarget.remove();
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
